<template>
  <div id="tooltip_overlay">
    <info-tooltip />
  </div>
  <app-popup :open="is_popup_open" @dismiss="is_popup_open=false">
    <div class="columns" id="rope_popup_content">
      <div class="column is-one-quarter">
        <div>
          <div class="dark_circle"> 1 </div>
        </div>
        <div class="has-text-black">
          <b>Select a focal month</b>
        </div>
        <img :src="step1img" :alt="step_alt_text['step_1']" />
      </div>
      <div class="column is-two-quarter">
        <div>
          <div class="dark_circle"> 2 </div>
        </div>
        <div class="has-text-black">
          <b>Interpret the visualization</b>
        </div>
        <img :src="step2img" :alt="step_alt_text['step_3']" />
      </div>
      <div class="column is-one-quarter">
        <div>
          <div class="dark_circle"> 3 </div>
        </div>
        <div class="has-text-centered has-text-black">
          <b>Adjust the visualization</b>
        </div>
        <img :src="step3img" :alt="step_alt_text['step_3']" />
      </div>
    </div>
  </app-popup>

  <RopesHeader v-model:all_supplement_topics="all_supplement_topics" v-model:is_popup_open="is_popup_open" />

  <calendar-grid :monthComponent="Month" :start-year="start_year" :end-year="end_year" @month-click="build_cohort">
  </calendar-grid>
</template>

<script setup>
import { inject, ref, onMounted, provide } from 'vue';
import Month from "./Month.vue";
import AppPopup from "./AppPopup.vue";
import RopesHeader from "./RopesHeader.vue";
import step1img from "../images/step1_ropes.png";
import step2img from "../images/step2_ropes.png";
import step3img from "../images/step3_ropes.png";
import CalendarGrid from "./CalendarGrid.vue";
import {get_cps_supplements, makeTooltipData, useSelectedSampleTopics} from "../lib/helpers";
import InfoTooltip from "./InfoTooltip.vue";

// data
const start_year = ref(1976);
const end_year = ref(new Date().getFullYear());
const grid = ref([]);
const all_supplement_topics = ref({});          // {"ASEC": {index:2, name: "ASEC", picked: true}, }
const cps_sample_list = ref(null);
const cohort_root = ref(null);
const previous_year_rotations = ref([]);
const this_year_rotations = ref([]);
const next_year_rotations = ref([]);

const tooltip_data = ref(makeTooltipData());
const is_loading = inject("is_loading");
const is_popup_open = inject('is_popup_open');

const step_alt_text = {
  step_1: "Click any month in calendar to set focal month for rotation pattern.",
  step_2: "For a given month, view cohort MIS, % individuals linkable from focal month, and supplement topic.",
  step_3: "Adjust visualization to show cohorts included in, starting, or finishing during selected focal month."
};

provide("atusCalendarGrid", grid);
provide('cps_sample_list', cps_sample_list);
provide('cps_supplement_topics', all_supplement_topics);
provide("tooltip_data", tooltip_data);
provide("viewing_options", ref([]));
provide("selected_viewing_option", ref(null));
provide("getSelectedSampleTopics", getSelectedSampleTopics);

const build_grid = () => {
  const temp_grid = {};
  for(let year_index = start_year.value; year_index <= end_year.value; year_index++){
    const year = {year: year_index, months: []};
    for(let month_index = 0; month_index < 12; month_index++){
      var month = {month_index: month_index, year: year.year, active: false, rotations: [], topics: [], cohorts:[]};
      year.months.push(month);
    }
    temp_grid[year.year] = year;
  }
  grid.value = temp_grid;
}

const build_cohort = (year, month) => {
  const focal_year = grid.value[year];
  const focal_month = focal_year.months[month];

  if((focal_month?.year === cohort_root.value?.year) && (focal_month?.month_index === cohort_root.value?.month_index)){
    return;
  }
  var previous_year_cohorts = grid.value[focal_month.year-1] === undefined ? null : generate_cohort_row(grid.value[focal_month.year-1].months[focal_month.month_index]);
  var this_year_cohorts     = generate_cohort_row(focal_month);
  var next_year_cohorts     = grid.value[focal_month.year+1] === undefined ? null : generate_cohort_row(grid.value[focal_month.year+1].months[focal_month.month_index]);

  if(cohort_root.value !== null){
    // reset old cohort data
    clear_rotations(cohort_root.value);
    cohort_root.value.active = false;
    cohort_root.value = null;
  }

  cohort_root.value = focal_month;
  cohort_root.value.active = true;
  // build new cohort
  if(previous_year_cohorts !== null){
    apply_rotations(previous_year_cohorts, previous_year_rotations.value)
  };
  apply_rotations(this_year_cohorts, this_year_rotations.value);
  if(next_year_cohorts !== null){
    apply_rotations(next_year_cohorts, next_year_rotations.value)
  };
  cohort_root.value.cohorts.push(previous_year_cohorts, this_year_cohorts, next_year_cohorts);
}

const previous_month = (month) => {
  if(month === null) {return null}

  var index_of_month = month.month_index;
  if((index_of_month - 1) < 0){
    var previous_year = month.year - 1;
    return grid.value[previous_year] === undefined ? null : grid.value[previous_year].months[11];
  }else{
    return grid.value[month.year].months[index_of_month - 1];
  }
}

const next_month = (month) => {
  if(month === null) {return null}

  var index_of_month = month.month_index;
  if((index_of_month + 1) > 11){
    var next_year = month.year + 1;
    return grid.value[next_year] === undefined ? null : grid.value[next_year].months[0];
  }else{
    return grid.value[month.year].months[index_of_month + 1];
  }
}

const apply_rotations = (cohort_array, class_array) => {
  for(let i=0; i<cohort_array.length; i++){
    if(cohort_array[i] !== null){
      cohort_array[i].rotations = class_array[i];
      cohort_array[i].active = true;
    }
  }
}

const clear_rotations = (month) => {
  for(let y = 0; y < month.cohorts.length; y++){
    if(month.cohorts[y] !== null){
      for(let m = 0; m < month.cohorts[y].length; m++){
        if(month.cohorts[y][m] !== null){
          month.cohorts[y][m].rotations = [];
          month.cohorts[y][m].active = false;
        }
      };
    };
  };
  month.cohorts = [];
}

const generate_cohort_row = (month) => {
  return [previous_month(previous_month(previous_month(month))), previous_month(previous_month(month)), previous_month(month),
    month,
    next_month(month), next_month(next_month(month)), next_month(next_month(next_month(month)))
  ]
}

const build_rotations = () => {
  previous_year_rotations.value =  [["A1"], ["A2","B1"], ["A3","B2","C1"], ["A4","B3","C2","D1"], ["B4","C3","D2"], ["C4","D3"], ["D4"]];
  this_year_rotations.value     =  [["A5","E1"], ["A6","B5","E2","F1"], ["A7","B6","C5","E3","F2","G1"], ["A8","B7","C6","D5","E4","F3","G2","H1"], ["B8","C7","D6","F4","G3","H2"], ["C8","D7","G4","H3"], ["D8","H4"]];
  next_year_rotations.value     =  [["E5"], ["E6","F5"], ["E7","F6","G5"], ["E8","F7","G6","H5"], ["F8","G7","H6"], ["G8","H7"], ["H8"]];
}

function getSelectedSampleTopics(year, month) {
  return useSelectedSampleTopics({
    cps_sample_list,
    cps_supplement_topics: all_supplement_topics,
    year: ref(year),
    month: ref(month),
  });
}

build_grid();
build_rotations();

onMounted(async () => {
  cps_sample_list.value = await get_cps_supplements(all_supplement_topics, grid);
  is_loading.value = false;
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  #tooltip_overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    pointer-events: none;
  }
</style>
